/**
 * Calcule quelles colonnes et quelles lignes
 * peuvent être affichées dans le canvas en ne considérant
 * que les cases entièrement visibles.
 * 
 * Les cases qui ne tiennent pas complètement dans le canvas
 * seront considérées comme supprimées.
 *
 * Dans cette version, si le nombre de cases à supprimer est impair,
 * on retire une case supplémentaire afin d'obtenir une répartition
 * symétrique (même si cela fait perdre une case de trop qui serait visible).
 * 
 * @param caseSize La taille de base (en px) pour 1 case
 * @param maxCanvasWidth La largeur disponible du canvas (en px)
 * @param maxCanvasHeight La hauteur disponible du canvas (en px)
 * @param totalCols Nombre total de colonnes (16 par défaut)
 * @param totalRows Nombre total de lignes (12 par défaut)
 * @returns Un objet contenant les indices des colonnes/lignes visibles et supprimées
 */
export function computeVisibleGrid(
  caseSize: number,
  maxCanvasWidth: number = window.innerWidth,
  maxCanvasHeight: number = window.innerHeight,
  totalCols: number = 16,
  totalRows: number = 12
) {
  // console.log("computeVisibleGrid", caseSize, maxCanvasWidth, maxCanvasHeight, totalCols, totalRows);
  const widthMultiplier = 5.5 / 4;
  const cellWidth = widthMultiplier * caseSize;
  const cellHeight = caseSize;

  let fullColumns = Math.floor(maxCanvasWidth / cellWidth);
  let fullRows = Math.floor(maxCanvasHeight / cellHeight);

  let columnsToRemoveLeft = 0, columnsToRemoveRight = 0;
  let rowsToRemoveTop = 0, rowsToRemoveBottom = 0;

  const visibleColumns: number[] = [];
  const visibleRows: number[] = [];

  // Colonnes
  if (fullColumns < totalCols) {
    // Pour une répartition strictement symétrique, on veut que
    // totalCols - fullColumns soit pair. Sinon, on retire 1 colonne de plus.
    if ((totalCols - fullColumns) % 2 !== 0) {
      fullColumns--; // on renonce à 1 colonne visible pour obtenir la symétrie
    }
    const diffColumns = totalCols - fullColumns;
    columnsToRemoveLeft = diffColumns / 2;
    columnsToRemoveRight = diffColumns / 2;
    for (let i = columnsToRemoveLeft; i < totalCols - columnsToRemoveRight; i++) {
      visibleColumns.push(i);
    }
  } else {
    // Tout tient dans le canvas
    for (let i = 0; i < totalCols; i++) {
      visibleColumns.push(i);
    }
  }

  // Lignes
  if (fullRows < totalRows) {
    if ((totalRows - fullRows) % 2 !== 0) {
      fullRows--; // on renonce à 1 ligne visible pour obtenir la symétrie
    }
    const diffRows = totalRows - fullRows;
    rowsToRemoveTop = diffRows / 2;
    rowsToRemoveBottom = diffRows / 2;
    for (let i = rowsToRemoveTop; i < totalRows - rowsToRemoveBottom; i++) {
      visibleRows.push(i);
    }
  } else {
    for (let i = 0; i < totalRows; i++) {
      visibleRows.push(i);
    }
  }

  // Colonnes supprimées
  const removedColumns: number[] = [];
  for (let i = 0; i < columnsToRemoveLeft; i++) {
    removedColumns.push(i);
  }
  for (let i = totalCols - columnsToRemoveRight; i < totalCols; i++) {
    removedColumns.push(i);
  }

  // Lignes supprimées
  const removedRows: number[] = [];
  for (let i = 0; i < rowsToRemoveTop; i++) {
    removedRows.push(i);
  }
  for (let i = totalRows - rowsToRemoveBottom; i < totalRows; i++) {
    removedRows.push(i);
  }

  return {
    visibleColumns,
    visibleRows,
    removedColumns,
    removedRows,
  };
}
